import React, { useState } from 'react';
import type {
	EditWidgetRowProps,
	WidgetDataSourceProps,
	WidgetRow
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { Button, Flex, Input, Select, Text, Tooltip, Checkbox } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import WidgetFieldSetting from './helpers/WidgetFieldSetting';
import useExternalIntegrations from 'App/components/hooks/integrations/useExternalIntegrations';
import { removeBracketsFromTag } from './UiWidgetHelpers';
import type { Order } from 'App/resources/Model/Order';
import CodeEditor from 'App/babel/components/CodeEditor';
import { get } from 'lodash';
import { EditUiWidgetRow } from './UiWidgetRow';
import UiWidgetRow from './UiWidgetRow';
import { handleWidgetResponse, triggerDataSource } from './helpers/widgetResponseHandler';

type FieldActions = {
	updated?: {
		order: Partial<Order>;
	};
	disabled?: {
		order: { [key: string]: boolean | FieldActions };
	};
	visible?: {
		order: { [key: string]: boolean | FieldActions };
	};
};

export const EditUiWidgetButton = ({ row, onUpdate, dataSources }: EditWidgetRowProps) => {
	const { t } = useTranslation();
	const text = row.settings?.text || '';
	const selectedDataSourceName = row.settings?.dataSource || '';
	const enableLoop = row.settings?.enableLoop || false;
	const responseHandling = row.settings?.responseHandling || 'none';
	const responseMapping = row.settings?.responseMapping || '';
	const hideIfFieldEmpty = row.settings?.hideIfFieldEmpty || '';

	const dataSourceOptions = Object.keys(dataSources || {}).map(name => ({ id: name, title: name }));
	const selectedOption = dataSourceOptions.find(opt => opt.id === selectedDataSourceName) || null;

	const responseHandlingOptions = [
		{ id: 'none', title: t('default.responseHandlingNone') },
		{ id: 'updateOrder', title: t('default.responseHandlingUpdateOrder') },
		{ id: 'showRows', title: t('default.responseHandlingShowRows') }
	];
	const selectedResponseHandling =
		responseHandlingOptions.find(opt => opt.id === responseHandling) || responseHandlingOptions[0];

	const handleChange = (field: string, value: string | boolean | Record<string, any>) => {
		onUpdate({
			...row,
			settings: {
				...row.settings,
				[field]: value
			}
		});
	};

	const handleResponseMappingChange = (editorState: any) => {
		const jsonString = editorState.code;
		handleChange('responseMapping', jsonString);
	};

	return (
		<Flex direction="column" gap="u2">
			<WidgetFieldSetting text={t('default.text')}>
				<Input value={text} onChange={e => handleChange('text', e.target.value)} />
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.dataSource')}>
				<Select
					value={selectedOption}
					options={dataSourceOptions}
					onChange={opt => handleChange('dataSource', opt?.id)}
					anchor=".Modals"
				/>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.loopOrderRows')}>
				<Tooltip title={t('default.loopOrderRowsTooltip')}>
					<Checkbox checked={enableLoop} onChange={checked => handleChange('enableLoop', checked)} />
				</Tooltip>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.responseHandling')}>
				<Tooltip title={t('default.responseHandlingTooltip')}>
					<Select
						value={selectedResponseHandling}
						options={responseHandlingOptions}
						onChange={opt => handleChange('responseHandling', opt?.id)}
						anchor=".Modals"
					/>
				</Tooltip>
			</WidgetFieldSetting>

			{responseHandling === 'updateOrder' ? (
				<WidgetFieldSetting text={t('default.responseMapping')}>
					<Tooltip title={t('default.responseMappingTooltip')}>
						<CodeEditor
							value={responseMapping}
							onChange={handleResponseMappingChange}
							mode="json"
							theme="cobalt"
							maxLines={10}
							showGutter={true}
						/>
					</Tooltip>
				</WidgetFieldSetting>
			) : null}

			{responseHandling === 'showRows' ? (
				<WidgetFieldSetting text={t('default.rowConfiguration')}>
					<EditUiWidgetRow
						row={{ type: 'Row', settings: row.settings?.rowConfiguration || {} } as WidgetRow}
						onUpdate={(updatedRow: WidgetRow) => handleChange('rowConfiguration', updatedRow.settings)}
						dataSources={dataSources}
					/>
				</WidgetFieldSetting>
			) : null}

			<WidgetFieldSetting text={t('default.hideIfFieldEmpty')}>
				<Tooltip title={t('default.hideIfFieldEmptyTooltip')}>
					<Input value={hideIfFieldEmpty} onChange={e => handleChange('hideIfFieldEmpty', e.target.value)} />
				</Tooltip>
			</WidgetFieldSetting>
		</Flex>
	);
};

const UiWidgetButton: React.FC<
	WidgetDataSourceProps & { updateOrderFromApp?: (fieldActions: FieldActions, order?: Order) => void }
> = ({ row, widgetExternalId, dataSources, widgetSettings, dataSourceSettings, updateOrderFromApp }) => {
	const { t } = useTranslation();
	const { getDataSource, user } = useExternalIntegrations();
	const [isLoading, setIsLoading] = useState(false);
	const [responseData, setResponseData] = useState<any>(null);

	const buttonText = row.settings?.text || t('default.button');
	const dataSourceName: string | undefined = row.settings?.dataSource;
	const enableLoop = row.settings?.enableLoop || false;
	const hideIfFieldEmpty = row.settings?.hideIfFieldEmpty;
	const responseHandling = row.settings?.responseHandling || 'none';

	const handleResponse = (responses: any[]) => {
		handleWidgetResponse({
			responses,
			responseHandling,
			responseMapping: row.settings?.responseMapping,
			dataSources,
			widgetExternalId,
			widgetSettings,
			user,
			updateOrderFromApp,
			setResponseData,
			errorContext: 'button'
		});
	};

	const handleClick = async () => {
		await triggerDataSource({
			dataSourceName: dataSourceName!,
			dataSourceSettings: dataSourceSettings!,
			enableLoop,
			dataSources,
			widgetExternalId,
			user,
			getDataSource,
			handleResponse,
			setIsLoading,
			errorContext: 'button'
		});
	};

	const isDisabled = !dataSourceName || isLoading;

	if (hideIfFieldEmpty) {
		// Need to wait for dataSources to be loaded, hiding button in the meantime
		if (!dataSources) {
			return null;
		}
		const fieldPath = removeBracketsFromTag(hideIfFieldEmpty);
		const customFieldMatch = fieldPath.match(/^(.+\.custom)\.?\[(?:'|")(.+?)(?:'|")\]$/);

		let fieldValue;
		if (customFieldMatch) {
			const basePath = customFieldMatch[1];
			const fieldName = customFieldMatch[2];
			const customFields = get(dataSources, basePath) as any[];

			if (Array.isArray(customFields)) {
				const field = customFields.find(f => f.name === fieldName);
				fieldValue = field?.value;
			}

			if (!fieldValue) {
				const mappedCustomPath = basePath.replace('.custom', '.$mappedCustom');
				const mappedCustomFields = get(dataSources, mappedCustomPath) as Record<string, any>;

				if (mappedCustomFields && typeof mappedCustomFields === 'object') {
					const matchingField = Object.values(mappedCustomFields).find(
						(field: any) => field?.name === fieldName
					);
					if (matchingField) {
						fieldValue = matchingField.value;
					}
				}
			}
		} else {
			fieldValue = get(dataSources, fieldPath);
		}

		if (!fieldValue) {
			return null;
		}
	}

	const rowConfiguration = row.settings?.rowConfiguration;

	return (
		<Flex direction="column" gap="u2">
			<Tooltip title={isDisabled ? t('default.dataSource') : ''} disabled={!isDisabled}>
				<Button onClick={handleClick} disabled={isDisabled} loading={isLoading}>
					<Text color="inherit">{buttonText}</Text>
				</Button>
			</Tooltip>

			{responseHandling === 'showRows' && responseData && rowConfiguration ? (
				<UiWidgetRow
					row={{ type: 'Row', settings: rowConfiguration } as WidgetRow}
					widgetExternalId={widgetExternalId}
					dataSources={{
						...dataSources,
						Response: responseData
					}}
					widgetSettings={widgetSettings}
					dataSourceSettings={dataSourceSettings}
				/>
			) : null}
		</Flex>
	);
};

export default UiWidgetButton;
